// Keeping a running client on the latest build.
//
// The app is a PWA: Workbox precaches the built shell, so a page load is
// served from the service worker's cache and a new deploy only shows once a
// NEW service worker has installed and taken over. With the plugin's default
// "autoUpdate" that took two reloads (one to fetch the new worker, one to be
// served by it) and could also swap the worker mid-match.
//
// Now the worker is registered in "prompt" mode: a new build installs in the
// background and WAITS. We apply it (skip waiting + reload) ourselves, the
// moment there is a new one, unless a match is on screen; then we hold until
// the player is back in the shell. No prompt, no chip: a quiet reload at a
// harmless moment. We also ask the browser to look for a new worker whenever
// the tab comes back into view and every few minutes, so a phone that keeps
// the tab alive for days still picks up deploys.
import { registerSW } from "virtual:pwa-register";

const CHECK_EVERY_MS = 5 * 60 * 1000;
const RETRY_MS = 2000;

// A match (live arena, round-over, replay) or a custom lobby is on screen:
// they all render inside the in-game shell wrapper, and the lobby keeps a
// live session we must not drop.
function inGame(): boolean {
  return !!document.querySelector('.page-main-game, [class*="game-stage"]');
}

// The player is typing (a name, a lobby code): don't yank the page away.
function typing(): boolean {
  const el = document.activeElement;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || (el as HTMLElement).isContentEditable);
}

export function startPwaUpdates(): void {
  // The app booted, so the bundle loaded fine: re-arm the one-shot cache-poisoning
  // recovery in index.html for any later failure (e.g. a poisoned lazy chunk).
  try { sessionStorage.removeItem("achtungSwRecovery"); } catch { /* storage off */ }
  if (!("serviceWorker" in navigator)) return;
  // The mark that this browser runs a page that applies updates itself: without
  // it a new worker takes over at once (public/sw-takeover.js), for the pages
  // from before this file that never tell it to.
  try { void caches.open("ar-update-prompt").catch(() => {}); } catch { /* no Cache Storage */ }
  let apply: ((reload?: boolean) => Promise<void>) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const tryApply = () => {
    if (!apply) return;
    // (nor while a framed game waits for its sign-in window, shared/auth-frame.ts)
    if (inGame() || typing() || document.visibilityState !== "visible" || (window as any).__arSignInPending) {
      timer = setTimeout(tryApply, RETRY_MS);
      return;
    }
    const fn = apply;
    apply = null;
    void fn(true); // tells the waiting worker to take over, then reloads
  };

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      apply = updateSW;
      if (timer) clearTimeout(timer);
      tryApply();
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => {
        // (an update check can fail harmlessly: offline for a moment, or the worker
        // being replaced; Sentry ACHTUNG-ROYALE-6/B. The next check tries again.)
        if (document.visibilityState === "visible" && navigator.onLine !== false) registration.update().catch(() => {});
      };
      setInterval(check, CHECK_EVERY_MS);
      document.addEventListener("visibilitychange", check);
    },
  });
}
